
import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AboutAdmin = () => {
  const [aboutData, setAboutData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('section1');
  
  // Initialize form state for section 1
  const [section1Data, setSection1Data] = useState({
    subtitle: "",
    title: "",
    description: "",
    features: [{ text: "" }],
    buttonText: "",
    buttonLink: "",
    images: [{ url: "", alt: "" }, { url: "", alt: "" }],
    isActive: true,
  });
  
  // Initialize form state for section 2
  const [section2Data, setSection2Data] = useState({
    subtitle: "",
    title: "",
    description: "",
    features: [{ text: "" }],
    buttonText: "",
    buttonLink: "",
    images: [{ url: "", alt: "" }, { url: "", alt: "" }],
    isActive: true,
  });

  // Fetch about data
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/about-section');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setAboutData(result.data);
          
          // Initialize form data with fetched data
          if (result.data.section1) {
            setSection1Data({
              subtitle: result.data.section1.subtitle || "",
              title: result.data.section1.title || "",
              description: result.data.section1.description || "",
              features: result.data.section1.features || [{ text: "" }],
              buttonText: result.data.section1.buttonText || "",
              buttonLink: result.data.section1.buttonLink || "",
              images: result.data.section1.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
              isActive: result.data.section1.isActive !== undefined ? result.data.section1.isActive : true,
            });
          }
          
          if (result.data.section2) {
            setSection2Data({
              subtitle: result.data.section2.subtitle || "",
              title: result.data.section2.title || "",
              description: result.data.section2.description || "",
              features: result.data.section2.features || [{ text: "" }],
              buttonText: result.data.section2.buttonText || "",
              buttonLink: result.data.section2.buttonLink || "",
              images: result.data.section2.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
              isActive: result.data.section2.isActive !== undefined ? result.data.section2.isActive : true,
            });
          }
        } else {
          setError(result.message || 'Failed to fetch about data');
        }
      } catch (err) {
        console.error('Error fetching about data:', err);
        setError('Network error or server not responding');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAboutData();
  }, []);

  // Update form data when active section changes
  useEffect(() => {
    if (aboutData) {
      if (activeSection === 'section1' && aboutData.section1) {
        setSection1Data({
          subtitle: aboutData.section1.subtitle || "",
          title: aboutData.section1.title || "",
          description: aboutData.section1.description || "",
          features: aboutData.section1.features || [{ text: "" }],
          buttonText: aboutData.section1.buttonText || "",
          buttonLink: aboutData.section1.buttonLink || "",
          images: aboutData.section1.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
          isActive: aboutData.section1.isActive !== undefined ? aboutData.section1.isActive : true,
        });
      } else if (activeSection === 'section2' && aboutData.section2) {
        setSection2Data({
          subtitle: aboutData.section2.subtitle || "",
          title: aboutData.section2.title || "",
          description: aboutData.section2.description || "",
          features: aboutData.section2.features || [{ text: "" }],
          buttonText: aboutData.section2.buttonText || "",
          buttonLink: aboutData.section2.buttonLink || "",
          images: aboutData.section2.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
          isActive: aboutData.section2.isActive !== undefined ? aboutData.section2.isActive : true,
        });
      }
    }
  }, [activeSection, aboutData]);

  // Handle input changes for section 1
  const handleSection1Change = (e, index = null) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'features') {
      const newFeatures = [...section1Data.features];
      newFeatures[index].text = value;
      setSection1Data(prev => ({ ...prev, features: newFeatures }));
    } else if (name === 'imageUrl' || name === 'imageAlt') {
      const newImages = [...section1Data.images];
      const imageIndex = parseInt(e.target.dataset.index);
      
      if (name === 'imageUrl') {
        newImages[imageIndex].url = value;
      } else {
        newImages[imageIndex].alt = value;
      }
      
      setSection1Data(prev => ({ ...prev, images: newImages }));
    } else {
      setSection1Data(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle input changes for section 2
  const handleSection2Change = (e, index = null) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'features') {
      const newFeatures = [...section2Data.features];
      newFeatures[index].text = value;
      setSection2Data(prev => ({ ...prev, features: newFeatures }));
    } else if (name === 'imageUrl' || name === 'imageAlt') {
      const newImages = [...section2Data.images];
      const imageIndex = parseInt(e.target.dataset.index);
      
      if (name === 'imageUrl') {
        newImages[imageIndex].url = value;
      } else {
        newImages[imageIndex].alt = value;
      }
      
      setSection2Data(prev => ({ ...prev, images: newImages }));
    } else {
      setSection2Data(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Add a new feature to section 1
  const addFeatureToSection1 = () => {
    setSection1Data(prev => ({
      ...prev,
      features: [...prev.features, { text: "" }]
    }));
    toast.info("Added new feature to Section 1");
  };

  // Remove a feature from section 1
  const removeFeatureFromSection1 = (index) => {
    if (section1Data.features.length <= 1) {
      toast.error("At least one feature is required");
      return;
    }
    
    const newFeatures = [...section1Data.features];
    newFeatures.splice(index, 1);
    setSection1Data(prev => ({ ...prev, features: newFeatures }));
    toast.info("Removed feature from Section 1");
  };

  // Add a new feature to section 2
  const addFeatureToSection2 = () => {
    setSection2Data(prev => ({
      ...prev,
      features: [...prev.features, { text: "" }]
    }));
    toast.info("Added new feature to Section 2");
  };

  // Remove a feature from section 2
  const removeFeatureFromSection2 = (index) => {
    if (section2Data.features.length <= 1) {
      toast.error("At least one feature is required");
      return;
    }
    
    const newFeatures = [...section2Data.features];
    newFeatures.splice(index, 1);
    setSection2Data(prev => ({ ...prev, features: newFeatures }));
    toast.info("Removed feature from Section 2");
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Validate required fields
      if (!section1Data.title || !section2Data.title) {
        toast.error("Section titles are required");
        setIsSaving(false);
        return;
      }
      
      const aboutSectionData = {
        section1: section1Data,
        section2: section2Data,
        isActive: true,
        updatedAt: new Date(),
      };
      
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/about-section', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(aboutSectionData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('About section updated successfully!');
        setAboutData(result.data);
      } else {
        toast.error(result.message || 'Failed to update about section');
      }
    } catch (err) {
      console.error('Error updating about section:', err);
      toast.error('Network error or server not responding');
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle about section active status
  const handleToggleActive = async () => {
    setIsToggling(true);
    
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/about-section/toggle', {
        method: 'PATCH',
      });
      
      const result = await response.json();
      
      if (result.success) {
        const status = result.data.isActive ? 'activated' : 'deactivated';
        toast.success(`About section ${status} successfully!`);
        setAboutData(result.data);
      } else {
        toast.error(result.message || 'Failed to toggle about section status');
      }
    } catch (err) {
      console.error('Error toggling about section status:', err);
      toast.error('Network error or server not responding');
    } finally {
      setIsToggling(false);
    }
  };

  // Reset about section to default
  const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset the about section to default settings?')) {
      return;
    }
    
    setIsResetting(true);
    
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/about-section/reset', {
        method: 'POST',
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('About section reset to default successfully!');
        setAboutData(result.data);
        
        // Reset form data
        setSection1Data({
          subtitle: result.data.section1.subtitle || "",
          title: result.data.section1.title || "",
          description: result.data.section1.description || "",
          features: result.data.section1.features || [{ text: "" }],
          buttonText: result.data.section1.buttonText || "",
          buttonLink: result.data.section1.buttonLink || "",
          images: result.data.section1.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
          isActive: result.data.section1.isActive !== undefined ? result.data.section1.isActive : true,
        });
        
        setSection2Data({
          subtitle: result.data.section2.subtitle || "",
          title: result.data.section2.title || "",
          description: result.data.section2.description || "",
          features: result.data.section2.features || [{ text: "" }],
          buttonText: result.data.section2.buttonText || "",
          buttonLink: result.data.section2.buttonLink || "",
          images: result.data.section2.images || [{ url: "", alt: "" }, { url: "", alt: "" }],
          isActive: result.data.section2.isActive !== undefined ? result.data.section2.isActive : true,
        });
      } else {
        toast.error(result.message || 'Failed to reset about section');
      }
    } catch (err) {
      console.error('Error resetting about section:', err);
      toast.error('Network error or server not responding');
    } finally {
      setIsResetting(false);
    }
  };

  // Deactivate about section
  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate the about section?')) {
      return;
    }
    
    setIsDeleting(true);
    
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/about-section', {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('About section deactivated successfully!');
        setAboutData(prev => ({ ...prev, isActive: false }));
      } else {
        toast.error(result.message || 'Failed to deactivate about section');
      }
    } catch (err) {
      console.error('Error deactivating about section:', err);
      toast.error('Network error or server not responding');
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading about data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4>Error Loading About Data</h4>
        <p>{error}</p>
        <button 
          className="btn btn-outline-danger"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  // Render section form
  const renderSectionForm = (sectionData, handleSectionChange, addFeature, removeFeature, sectionNumber) => {
    return (
      <div className="card mb-4">
        <div className="card-header ">
          <h5 className="text-black">Section {sectionNumber} {sectionNumber === 1 ? '(Image Left, Content Right)' : '(Content Left, Image Right)'}</h5>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              name="isActive"
              checked={sectionData.isActive}
              onChange={handleSectionChange}
            />
            <label className="form-check-label" htmlFor="isActive">
              Section is active
            </label>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Subtitle</label>
              <input
                type="text"
                className="form-control"
                name="subtitle"
                value={sectionData.subtitle}
                onChange={handleSectionChange}
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={sectionData.title}
                onChange={handleSectionChange}
                required
              />
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                rows="3"
                value={sectionData.description}
                onChange={handleSectionChange}
                required
              ></textarea>
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Features</label>
              {sectionData.features.map((feature, index) => (
                <div key={index} className="input-group mb-2">
                  <span className="input-group-text">
                    <i className="bx bx-check" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="features"
                    value={feature.text}
                    onChange={(e) => handleSectionChange(e, index)}
                    placeholder="Feature text"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => sectionNumber === 1 ? removeFeatureFromSection1(index) : removeFeatureFromSection2(index)}
                    disabled={sectionData.features.length <= 1}
                  >
                    <i className="bx bx-trash" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={sectionNumber === 1 ? addFeatureToSection1 : addFeatureToSection2}
              >
                <i className="bx bx-plus me-1" /> Add Feature
              </button>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Button Text</label>
              <input
                type="text"
                className="form-control"
                name="buttonText"
                value={sectionData.buttonText}
                onChange={handleSectionChange}
              />
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Button Link</label>
              <input
                type="text"
                className="form-control"
                name="buttonLink"
                value={sectionData.buttonLink}
                onChange={handleSectionChange}
              />
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Images</label>
              {sectionData.images.map((image, index) => (
                <div key={index} className="card mb-3">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Image URL {index + 1}</label>
                        <input
                          type="text"
                          className="form-control"
                          name="imageUrl"
                          data-index={index}
                          value={image.url}
                          onChange={handleSectionChange}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Alt Text {index + 1}</label>
                        <input
                          type="text"
                          className="form-control"
                          name="imageAlt"
                          data-index={index}
                          value={image.alt}
                          onChange={handleSectionChange}
                          placeholder="Image description"
                        />
                      </div>
                      
                      {image.url && (
                        <div className="col-12 mb-3">
                          <label className="form-label">Preview</label>
                          <div>
                            <img
                              src={image.url}
                              alt={image.alt || `Preview ${index + 1}`}
                              className="img-thumbnail"
                              style={{ maxWidth: '200px' }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/200x200?text=Invalid+Image+URL';
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="about-admin">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="text-black">About Section Management</h3>
        <div>
          <button 
            className={`btn ${aboutData?.isActive ? 'btn-warning' : 'btn-success'} me-2`}
            onClick={handleToggleActive}
            disabled={isToggling}
          >
            {isToggling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {aboutData?.isActive ? 'Deactivating...' : 'Activating...'}
              </>
            ) : aboutData?.isActive ? 'Deactivate' : 'Activate'} About Section
          </button>
          <button 
            className="btn btn-danger me-2"
            onClick={handleDeactivate}
            disabled={isDeleting || !aboutData?.isActive}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : 'Delete About Section'}
          </button>
          <button 
            className="btn btn-secondary mt-2"
            onClick={handleResetToDefault}
            disabled={isResetting}
          >
            {isResetting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Resetting...
              </>
            ) : 'Reset to Default'}
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeSection === 'section1' ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveSection('section1')}
            >
              Section 1
            </button>
          </li>
          {/* <li className="nav-item">
            <button 
              className={`nav-link ${activeSection === 'section2' ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveSection('section2')}
            >
              Section 2
            </button>
          </li> */}
        </ul>
        
        {activeSection === 'section1' && renderSectionForm(
          section1Data, 
          handleSection1Change, 
          addFeatureToSection1, 
          removeFeatureFromSection1, 
          1
        )}
        
        {activeSection === 'section2' && renderSectionForm(
          section2Data, 
          handleSection2Change, 
          addFeatureToSection2, 
          removeFeatureFromSection2, 
          2
        )}
        
        <div className="d-flex justify-content-end">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
      
      {aboutData && (
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="text-black">Current About Section Status</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Status:</strong> <span className={`badge ${aboutData.isActive ? 'bg-success' : 'bg-danger'}`}>
                  {aboutData.isActive ? 'Active' : 'Inactive'}
                </span></p>
                <p><strong>Last Updated:</strong> {new Date(aboutData.updatedAt).toLocaleString()}</p>
                <p><strong>Created:</strong> {new Date(aboutData.createdAt).toLocaleString()}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Section 1:</strong> <span className={`badge ${aboutData.section1?.isActive ? 'bg-success' : 'bg-secondary'}`}>
                  {aboutData.section1?.isActive ? 'Active' : 'Inactive'}
                </span></p>
                {/* <p><strong>Section 2:</strong> <span className={`badge ${aboutData.section2?.isActive ? 'bg-success' : 'bg-secondary'}`}>
                  {aboutData.section2?.isActive ? 'Active' : 'Inactive'}
                </span></p> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutAdmin;